import React, { useState, useMemo } from 'react';
import { 
  Clock, Star, Car, CaretDown, CaretUp, Trash, ArrowRight 
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { StatusBadge } from './GlassComponents';
import { haptics } from '../../utils/haptics';

export const SwipableRideCard = ({ 
  ride, 
  isExpanded, 
  onToggleExpand, 
  onDeleteClick, 
  onRateClick, 
  isDriver 
}) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const formattedDate = useMemo(() => {
    return new Date(ride.created_at).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, [ride.created_at]);

  const fareAmount = isDriver ? ride.driver_earning : ride.fare;

  // Dynamically calculate opacity of red delete panel (0 when resting, 1 when swiped)
  const deleteOpacity = Math.min(1, Math.abs(swipeOffset) / 35);

  return (
    <div 
      className="relative overflow-hidden border-b border-border w-full transition-colors last:border-b-0" 
      data-testid={`history-ride-${ride.id}`}
    >
      {/* Background Action: Revealed ONLY when sliding left */}
      <motion.div 
        animate={{ opacity: deleteOpacity }}
        transition={{ duration: 0.15 }}
        className={`absolute inset-y-0 right-0 w-24 bg-red-600 flex items-center justify-center z-0 ${
          swipeOffset < -5 ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            haptics.deleteThreshold();
            if (onDeleteClick) onDeleteClick(ride);
          }}
          className="flex flex-col items-center justify-center gap-1 w-full h-full text-white font-medium hover:bg-red-700 transition-colors cursor-pointer"
          title="Delete Ride"
        >
          <Trash weight="duotone" className="w-5 h-5 text-white" />
          <span className="text-[10px] font-bold uppercase tracking-wider">Delete</span>
        </button>
      </motion.div>

      {/* Foreground Draggable & Flat Collapsible Card */}
      <motion.div
        drag="x"
        dragConstraints={{ left: -90, right: 0 }}
        dragElastic={0.05}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={(e, info) => {
          setTimeout(() => setIsDragging(false), 150);
          if (info.offset.x < -30) {
            setSwipeOffset(-90);
            haptics.deleteThreshold();
          } else {
            setSwipeOffset(0);
          }
        }}
        animate={{ x: swipeOffset }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        className={`relative z-10 w-full bg-card hover:bg-muted/40 text-card-foreground transition-colors ${
          isExpanded ? 'bg-muted/20' : ''
        }`}
      >
        {/* Collapsed Item Summary View */}
        <div
          onClick={(e) => {
            if (isDragging) return;
            if (swipeOffset < 0) {
              setSwipeOffset(0);
            } else if (onToggleExpand) {
              onToggleExpand(ride.id);
            }
          }}
          className="py-4 px-4 md:px-6 cursor-pointer select-none"
        >
          <div className="flex items-center justify-between gap-2 mb-2.5">
            <div className="flex items-center gap-2">
              <Clock weight="duotone" className="w-4 h-4 text-gold" />
              <span className="text-xs md:text-sm font-medium text-muted-foreground">
                {formattedDate}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {/* Hide StatusBadge if status is 'requested' */}
              {ride.status && ride.status !== 'requested' && (
                <StatusBadge status={ride.status} />
              )}

              {/* Chevron Collapse Indicator */}
              <div className="p-0.5 text-muted-foreground">
                {isExpanded ? (
                  <CaretUp weight="bold" className="w-5 h-5 text-gold" />
                ) : (
                  <CaretDown weight="bold" className="w-5 h-5 text-muted-foreground" />
                )}
              </div>
            </div>
          </div>

          {/* Quick Route Preview */}
          <div className="flex items-center gap-3 mb-2.5 text-sm">
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <div className="w-2 h-2 rounded-full bg-gold shrink-0" />
              <span className="text-foreground font-medium truncate">
                {ride.pickup_location?.address || 'Pickup Location'}
              </span>
            </div>
            <ArrowRight weight="bold" className="w-4 h-4 text-muted-foreground shrink-0" />
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
              <span className="text-foreground font-medium truncate">
                {ride.dropoff_location?.address || 'Dropoff Location'}
              </span>
            </div>
          </div>

          {/* Collapsed Footer (Fare & Driver/Passenger Info) */}
          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-2">
              <Car weight="duotone" className="w-5 h-5 text-gold shrink-0" />
              <span className="text-xs text-muted-foreground font-medium truncate max-w-[150px] md:max-w-[220px]">
                {isDriver ? ride.student?.name || 'Passenger' : (ride.driver?.user?.name || ride.driver_name || (ride.status === 'cancelled' && !ride.driver_id ? 'No driver assigned' : 'Driver'))}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <span className={`text-[10px] uppercase font-bold tracking-wider ${
                ride.ride_mode === 'shared' 
                  ? 'text-blue-600 dark:text-blue-400' 
                  : 'text-amber-600 dark:text-amber-400'
              }`}>
                {ride.ride_mode === 'shared' ? 'Shared' : 'Private'}
              </span>
              <span className="text-gold font-heading font-bold text-base">
                K{fareAmount ? fareAmount.toFixed(2) : '0.00'}
              </span>
            </div>
          </div>
        </div>

        {/* Expanded Details Section */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className="overflow-hidden bg-muted/20 border-t border-border"
            >
              <div className="py-4 px-4 md:px-6 space-y-4">
                {/* Complete Route Timeline */}
                <div className="bg-muted/40 border border-border p-4 rounded-2xl space-y-3">
                  <h4 className="text-xs uppercase font-bold text-muted-foreground tracking-wider">
                    Full Trip Details
                  </h4>
                  <div className="space-y-3 relative pl-4 border-l border-dashed border-border">
                    <div className="relative">
                      <div className="absolute -left-[20px] top-1 w-3 h-3 rounded-full bg-gold" />
                      <p className="text-[11px] text-muted-foreground font-medium">Pickup Location</p>
                      <p className="text-sm font-semibold text-foreground mt-0.5">
                        {ride.pickup_location?.address || 'N/A'}
                      </p>
                    </div>
                    <div className="relative pt-1">
                      <div className="absolute -left-[20px] top-2 w-3 h-3 rounded-full bg-emerald-500" />
                      <p className="text-[11px] text-muted-foreground font-medium">Dropoff Location</p>
                      <p className="text-sm font-semibold text-foreground mt-0.5">
                        {ride.dropoff_location?.address || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Driver / Ride Specs Grid */}
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="bg-muted/40 border border-border p-3.5 rounded-2xl">
                    <p className="text-muted-foreground mb-1 text-[11px]">{isDriver ? 'Passenger' : 'Driver'}</p>
                    <p className="font-semibold text-foreground text-sm truncate">
                      {isDriver ? ride.student?.name || 'Passenger' : (ride.driver?.user?.name || ride.driver_name || (ride.status === 'cancelled' && !ride.driver_id ? 'No driver assigned' : 'N/A'))}
                    </p>
                    {!isDriver && ride.driver && (
                      <p className="text-muted-foreground text-[11px] font-medium mt-0.5 truncate">
                        {[ride.driver.vehicle_color, ride.driver.vehicle_model, ride.driver.plate_number].filter(Boolean).join(' • ')}
                      </p>
                    )}
                  </div>

                  <div className="bg-muted/40 border border-border p-3.5 rounded-2xl">
                    <p className="text-muted-foreground mb-1 text-[11px]">Distance & Fare</p>
                    <p className="font-semibold text-foreground text-sm">
                      {ride.distance ? ride.distance.toFixed(1) : '0'} km
                    </p>
                    <p className="text-gold font-bold text-[11px] mt-0.5">
                      K{fareAmount ? fareAmount.toFixed(2) : '0.00'} ({ride.ride_mode || 'standard'})
                    </p>
                  </div>
                </div>

                {/* Rating & Actions Bar */}
                <div className="flex items-center justify-between pt-1">
                  {ride.status === 'completed' ? (
                    <div className="flex items-center gap-2">
                      {ride.rating ? (
                        <div className="flex items-center gap-1.5 px-2 py-1">
                          <Star weight="duotone" className="w-4 h-4 text-gold fill-gold" />
                          <span className="text-xs font-bold text-gold">{ride.rating}.0 Rated</span>
                        </div>
                      ) : !isDriver && onRateClick ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRateClick(ride);
                          }}
                          className="px-4 py-2 rounded-xl bg-gold text-black font-semibold text-xs hover:bg-gold-dark transition-all cursor-pointer shadow-md"
                        >
                          ★ Rate Driver
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <div />
                  )}

                  {/* Red Delete Button in Expanded View - Clean Bold Text & Icon */}
                  {onDeleteClick && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteClick(ride);
                      }}
                      className="flex items-center gap-1.5 px-2 py-1 text-xs font-bold text-red-600 dark:text-red-500 hover:text-red-700 transition-colors cursor-pointer ml-auto"
                    >
                      <Trash weight="duotone" className="w-4 h-4 text-red-600 dark:text-red-500" />
                      Delete Record
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default SwipableRideCard;
